<template>
    <v-form @submit.prevent="addUpdate" ref="form">
        <v-card class="rounded-4">
            <v-toolbar flat height="45px">
                <span class="font-weight-bold ">{{mode}} Leave Benefit</span>
                <v-spacer></v-spacer>
                <help-link appId="compensation" pageId="add-leaves" color="black"></help-link>
                <v-btn icon @click.native="closeDialog()" >
                    <v-icon >close</v-icon>
                </v-btn>
            </v-toolbar>
            <v-alert v-if="error" :value="true" color="error" icon="error">
                Error encountered. Please contact support. Error details: {{errorMessage}}
            </v-alert> 
            <v-card-text>
                <v-layout row wrap> 
                    <v-flex xs12 sm4 class="form-row">
                        <v-select
                            label="Year"
                            :items="isEditMode?fiscal_years_all:fiscal_years"
                            :rules="[rules.required]" 
                            item-text="fiscalYear"
                            item-value="fiscalPeriodId"
                            :disabled="disabled||isEditMode"
                            v-model="fiscalPeriodId"
                        ></v-select>
                    </v-flex>
                    <v-flex xs12 sm8 class="form-row"> 
                        <v-select
                            label="Leave Type"
                            :items="leave_types"
                            :rules="[rules.required]" 
                            item-text="leaveName"
                            item-value="leaveTypeId"
                            :disabled="disabled||isEditMode"
                            v-model="leaveTypeId"
                            @change="checkAllowedFields"
                        ></v-select>
                    </v-flex>
                </v-layout>
                <v-layout row wrap> 
                    <v-flex xs12 sm6 class="form-row">                
                        <v-text-field label="Annual Credits (Days)" 
                            v-model="annualAllocation" 
                            :disabled="disabled"
                            :rules="[rules.required,rules.max_days]"
                            type="number"
                            min="1" maxlength="3"
                            hint="Total annual leave credits"
                            ></v-text-field>  
                    </v-flex>
                    <v-flex xs12 sm6 class="form-row">                    
                        <v-text-field label="Cash Conversion Limit (Days)" 
                            v-model="convertibleToCash" 
                            :disabled="disabled||!conversion_allowed"
                            :rules="[rules.required,rules.max_count]"
                            type="number"
                            min="1" maxlength="60"
                            hint="Unused leaves that can be converted to cash"
                            @change="checkLimit"
                            ></v-text-field>  
                    </v-flex>
                </v-layout>
                <v-layout row wrap> 
                    <v-flex xs12 sm6 class="form-row">               
                        <v-text-field label="Carry-Over Limit (Days)" 
                            v-model="toCarryOverNextYear" 
                            :disabled="disabled||!carryover_allowed"
                            :rules="[rules.required,rules.max_count]"
                            type="number"
                            min="1" maxlength="60"
                            hint="Unused leaves that can be carried over to next year"
                            @change="checkLimit"
                            ></v-text-field>  
                    </v-flex>
                    <v-flex xs12 sm6 class="form-row">            
                        <v-switch
                            v-model="prorated"
                            label="Pro-rated"
                            color="primary"
                            hide-details
                            :disabled="disabled||!prorated_allowed"
                        ></v-switch>   
                    </v-flex>
                </v-layout>
                <v-layout row wrap v-if="showComment"> 
                    <v-flex xs12 sm12 class="form-comments"> 
                        <v-textarea v-model="remarks" label="Comments" rows="2" color="primary"></v-textarea>
                    </v-flex>
                </v-layout>
            </v-card-text>
            <v-divider></v-divider>
            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn icon @click="showComment=!showComment" title="Add Comment">
                    <v-icon>comment</v-icon>
                </v-btn>
                <v-btn color="primary" type="submit" :disabled="disabled" :loading="loading" style="color:white">Save</v-btn>
                <v-btn v-if="mode=='Add'" @click="clearForm" :disabled="disabled" >Clear</v-btn>
            </v-card-actions>
        </v-card>
    </v-form>
</template>
<script>

import leaveTypesApi from '@/api/leavetypes'
import leavesAllocationApi from '@/api/leavesallocation'

export default {
    components: {
        'help-link': () => import('@/components/Common/HelpLink'),
    }, 
    data () {
        return {
            email: null,
            loading: false,
            disabled: false,
            error: false,
            errorMessage: null,
            rules: {
                required: value => !!value || 'Required field',
                email: value => {
                    const pattern = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
                    return pattern.test(value) || 'Invalid e-mail.'
                },
                max_count: value => value<=100 || 'Invalid Amount. Maximum of 100',
                max_days: value => value<=365 || 'Invalid Amount. Maximum of 365' 
            },
            mode: null,
            showComment: false,
            conversion_allowed: true,
            carryover_allowed: true,
            prorated_allowed: true,
            leavesAllocationId: null,
            userId: null,
            fiscalPeriodId: null,
            annualAllocation: null,
            convertibleToCash: null,
            toCarryOverNextYear: null,
            prorated: false,
            remarks: "",
            leaveTypeId: null,
            leave_types: []
        }
    },    
    methods: {
        checkLimit() {
            if (parseFloat(this.convertibleToCash) > parseFloat(this.annualAllocation))
                this.convertibleToCash = this.annualAllocation
            
            if (parseFloat(this.toCarryOverNextYear) > parseFloat(this.annualAllocation))
                this.toCarryOverNextYear = this.annualAllocation
        },
        checkAllowedFields() {
            var selected = this.leave_types.filter(x => {
                return x.leaveTypeId == this.leaveTypeId
            })
            if (selected.length>0) {
                this.conversion_allowed = selected[0].conversionAllowed
                this.carryover_allowed = selected[0].proratedAllowed
                this.prorated_allowed = selected[0].carryOverAllowed

                this.convertibleToCash = this.conversion_allowed?null:0
                this.toCarryOverNextYear = this.carryover_allowed?null:0
            }
        },
        async setAdd(data) {
            this.mode = 'Add'
            this.clearForm()
            this.userId = data.userId

            if (!this.leave_types.length) 
                await this.getLeaveTypes()
        },
        async setUpdate(data) {
            this.mode = 'Update'
            this.userId = data.userId

            if (!this.leave_types.length) 
                await this.getLeaveTypes()

            this.fiscalPeriodId = data.leaveBenefit.fiscalPeriodId
            this.leavesAllocationId = data.leaveBenefit.leavesAllocationId
            this.annualAllocation = data.leaveBenefit.annualAllocation
            this.convertibleToCash = data.leaveBenefit.convertibleToCash
            this.toCarryOverNextYear = data.leaveBenefit.toCarryOverNextYear
            this.leaveTypeId = data.leaveBenefit.leaveTypeId
            this.prorated = data.leaveBenefit.prorated
            this.remarks = ""
        },
        async addUpdate() {
            try {
                if (this.$refs.form.validate()) {
                    var data = {
                        leavesAllocationId: this.leavesAllocationId,
                        fiscalPeriodId: this.fiscalPeriodId,
                        userId: this.userId,
                        leaveTypeId: this.leaveTypeId,                        
                        annualAllocation : parseFloat(this.annualAllocation),
                        convertibleToCash : parseFloat(this.convertibleToCash),
                        toCarryOverNextYear : parseFloat(this.toCarryOverNextYear),
                        prorated : this.prorated,
                        remarks: this.remarks,

                        //these are used for update history only, not saved in the transaction table
                        leaveName: this.selectedLeaveName,
                        fiscalYear: this.selectedFiscalYear
                    }
                    var response = null
                    if (this.mode == 'Add') response = await leavesAllocationApi.add(data)
                    else response = await leavesAllocationApi.update(data)

                    if (response&&response.status==200) {
                        this.$emit('send',null)
                        this.closeDialog()
                    }
                }
            }catch(error){
                this.error = true
                this.errorMessage = error
            }
        },
        async getLeaveTypes() {
            try {
                this.loading = true
                this.disabled = true
                const response = await leaveTypesApi.getForAllocation() 
                if (response&&response.status==200) {
                    this.leave_types = response.data.items
                }
                this.loading = false
                this.disabled = false
            }catch(error){
                this.error = true
                this.errorMessage = error
            }
        },
        closeDialog() {
            this.$emit('update:dialogAddEditLeaves', false)
        },
        clearForm() {
            this.$refs.form.reset()
            this.loading = false
            this.error = false
            this.errorMessage = ''
            this.conversion_allowed = true
            this.carryover_allowed = true
            this.prorated_allowed = true
        },
    },
    computed: {
        fiscal_years() {
            const currentYear = new Date().getFullYear()
            if (this.$store.state.fiscalPeriods) {
                return this.$store.state.fiscalPeriods.filter(x => x.fiscalYear >= currentYear)
            }
            return [{fiscalYear: currentYear}]
        },
        fiscal_years_all() {
            const currentYear = new Date().getFullYear()
            if (this.$store.state.fiscalPeriods) {
                return this.$store.state.fiscalPeriods
            }
            return [{fiscalYear: currentYear}]
        },
        isEditMode() {
            return this.mode == 'Update'
        },
        selectedLeaveName() {
            if (this.leaveTypeId) 
                return this.leave_types.find(x => x.leaveTypeId == this.leaveTypeId).leaveName
        },
        selectedFiscalYear() {
            if (this.fiscalPeriodId)
                return this.fiscal_years_all.find(x => x.fiscalPeriodId == this.fiscalPeriodId).fiscalYear
        }
    }
}
</script>
<style scoped>
.form-row {
    padding:0px 20px 0px 20px
}
.form-comments {
    margin:5px 20px -25px 20px;
}
</style>